import update from 'immutability-helper';
import { isEmpty } from 'lodash';

const KEYWORDS = [
  'name' ,
  'customkey' ,
  'customvalue' ,
  'serial' ,
  'groupid' ,
  'categoryid',
  'group' ,
  'location' ,
  'osversion' ,
  'timezone' ,
  'analytics' ,
  'contentstatus' ,
  'contentsource' ,
  'status' ,
  'content' ,
  'orientation' ,
  'deviceorientation' ,
  'sitename' ,
  'filestatus' ,
  'regstatus' ,
  'devicemode' ,
  'mode',
  'batterylevel' ,
  'batteryhealth' ,
  'nowplaying' ,
  'tags' ,
  'batterypercentage',
  'os360status'
]

const OPERATORS = [
  'and not',
  'or not',
  'and',
  'or',
  'not',
]

/**
 *
 */
const removeFromSearchObjsByIndex = (
  origSearchObjs: any[] | undefined,
  removeIdx: number = -1
): any[]  => {
  if (!origSearchObjs || origSearchObjs.length === 0) return origSearchObjs || []

  if (removeIdx < 0 || removeIdx >= origSearchObjs.length ) return origSearchObjs

  const searchObjs = [...origSearchObjs]
  // The key:value pairs are in the even indices
  // Special case of index: 0
  if (removeIdx === 0) {
    searchObjs.splice(0, 2)
    return [...searchObjs]
  }
  if (removeIdx % 2 === 0) {
    // We will need to remove the idx and the one before that
    searchObjs.splice(removeIdx - 1, 2)
    return [...searchObjs]
  } else {
    // This means we are removing an operator
    searchObjs.splice(removeIdx, 2)
    return [...searchObjs]
  }
}

/**
 *
 * @param origSearchObjs
 * @param value
 * @param keyword
 * @param add
 * @param index : This is relevant only in delete
 * @returns
 */
const updateSearchObjs = (origSearchObjs: any[], value: string, keyword: string, add: boolean ): any[] => {

  if (!keyword || !value)
    return origSearchObjs

  let searchObjs = [...origSearchObjs]
  if(searchObjs[0] === null) return []
  const keywordIdx =  searchObjs.findIndex((o: Record<string, any>) => o.hasOwnProperty(keyword.toLowerCase()) )
  let valueData = value.split(":")[1] || value
  // strip the preceeding " or ending "
  valueData = valueData.replace(/^"/, '').replace(/"$/, '')

  if (add ===  true){
    // let us see if we have the index
    if (keywordIdx === -1){
      let searchObj: Record<string, any> = {}
      searchObj[keyword.toLowerCase()] = isOperator(keyword.toLowerCase())? keyword.toLowerCase() :valueData;
      return addToSearchObjs(searchObjs, searchObj)
    }
    // We have an index
    let searchObj: Record<string,any> = searchObjs[keywordIdx]
    let values = searchObj[keyword.toLowerCase()]
    if (Array.isArray(values)){
      if (values.indexOf(valueData) !== -1) // We are trying to add same value
        return searchObjs

      searchObj = update(searchObj, {[keyword.toLowerCase()]: {$set: [...values, valueData]}})
    }
    else{
      if (values === valueData) // We are trying to add same value as existing
        return searchObjs

      searchObj = update(searchObj, {[keyword.toLowerCase()]: {$set: [values, valueData]}})
    }
    searchObjs.splice(keywordIdx, 1, searchObj)
    return searchObjs
  }
  else {
    if (keywordIdx === -1)
      return searchObjs

    // We have an index
    let searchObj: Record<string,any> = searchObjs[keywordIdx]
    let values = searchObj[keyword.toLowerCase()]
    if (Array.isArray(values)){
      // We will need to check the index of the value
      const valueIdx = values.findIndex((v: string) => v === valueData)
      if (valueIdx === -1 )
        return searchObjs

      searchObj[keyword.toLowerCase()] = update(values, {$splice: [[ valueIdx, 1]]})

      if (searchObj[keyword.toLowerCase()].length === 1) // last value.. so we can ditch the array
        searchObj[keyword.toLowerCase()] = searchObj[keyword.toLowerCase()][0]

      let updSearchObj = Object.assign({}, searchObj)
      searchObjs.splice(keywordIdx, 1, updSearchObj)
      return [...searchObjs]
    }
    else {
      if (values === valueData) // the incoming value to remove does not match existing data
      {
        const spliceIdx = keywordIdx === 0 ? 0 : keywordIdx -1
        searchObjs.splice(spliceIdx,2)
      }

      if (values === undefined) // the keyword is an operator
        searchObjs.splice(keywordIdx, 1)

      return [...searchObjs]
    }
  }
}

/**
 *
 * @param searchObjs {existing search objects}
 * @param object {search object to add}
 * Note: search objects are of the form : {keyword: value} or {keyword: values}
 *
 * We will check if the last item in the search objects array is an operator. If not,
 * we will append the 'and' operator and the incoming search object
 * if last item is an operator, then we will do a plain append
 * if the search objects list is empty, we will do a plain append
 */
const addToSearchObjs = (searchObjs: any[], object: any): any[] => {
  if (searchObjs.length === 0)
    return update(searchObjs, {$push: [object]})

  const lastSearchObj: any = searchObjs[searchObjs.length -1];
  if (isOperatorObj(lastSearchObj) || isOperatorObj(object))
    return update(searchObjs, {$push: [object]})

  return update(searchObjs, {$push: [{and: 'and'}, object]})
}

/**
 * A query string can be for the form
 * - keyword:/s"value" [key_value]
 * - keyword IN ("value",..."value") [key_value]
 * - key_value OPERATOR key_value
 *
 * OPERATOR:
 * - AND
 * - OR
 * - AND NOT
 * - OR NOT
 * @param queryString
 * @returns
 */
const validateQueryString = (queryString: string): boolean => {
  if (queryString.trim() === '') return true
  const regExp = '(?<keyword>(?:'
  + KEYWORDS.join('|') +
  '))(?=(?::\\s*"(?<value>[^"]*(?=")))|(?:\\s+(?:in|not in)\\s+(?:\\((?<values>.*)\\))))(?:\\s+' +
  '(?<operator>(' + OPERATORS.join('|') + '))\\s+(\\k<keyword>)(?=(?::\\s*"\\k<value>)|(?:\\s+(?:in|not in)\\s+(?:\\(\\k<values>\\)))))*'

  const eqlRegEx : RegExp = new RegExp(regExp, 'gi')

  let eqlTest = eqlRegEx.test(queryString)

  if (eqlTest)
    return eqlTest;

  // We can also have a single word query ... Let us test for that
  const wordRegex : RegExp = new RegExp('(^\\w+$)', 'g')

  return wordRegex.test(queryString)
}

function parseQueryString(queryString: string): any {
  let searchObjs: any[] = []
  if (isEmpty(queryString)) return []
  const regexStr = '(?<keyword>(?:'
  + KEYWORDS.join('|')+
  '))(?=(?::\\s*"(?<value>[^"]+(?=")))|(?:\\s+(?:in|not in)\\s+(?:\\((?<values>[^)]+))))|' +
  '(?<operator>('+ OPERATORS.join(' |') + '))'
  const regex = new RegExp(regexStr, 'gi')
  let m
  // If we do not match the keyword value regex, that means it is a name or serial query
  // We need to instantiate a new Regex for the below test due to lastIndex pollution
  const checkRegex = new RegExp(regexStr, 'gi')
  if (!checkRegex.test(queryString)){
    searchObjs.push(queryString)
    return searchObjs
  }

  while ((m = regex.exec(queryString)) !== null) {
    // This is necessary to avoid infinite loops with zero-width matches
    if (m.index === regex.lastIndex) {
      regex.lastIndex++
    }

    // The result can be accessed through the `m`-variable.
    if (!m.groups)
      continue
    const groups = m.groups
    if (groups.notOperator){
      searchObjs.push({[groups.notOperator]: groups.notOperator})
      continue
    }

    if (groups.keyword){
      if (groups.value)
      {
        searchObjs.push({[groups.keyword]: groups.value})
      } else if (groups.values){
        const values = groups.values.split(",")
        .map((v) => v.trim()) // This will give as as follows "data"
        .map((v) => { return v.replace(/^"/, '')}) //will giev data"
        .map((v) => { return v.replace(/"$/, '')}) // will give data
        searchObjs.push({[groups.keyword]: values})
      }
    } else if (groups.operator){
      searchObjs.push({[groups.operator.trim()]: groups.operator.trim()})
    }
  }
  return searchObjs
}

/**
 * This will check if the query has the query term format
 * keyword:"value" or keyword in values
 * @param queryTerm
 */
const  extractQueryTerm = (queryTerm: string):any => {
  const termExp = '(?<keyword>(?:'
  + KEYWORDS.join('|') +
  '))(?=(?::\\s*"(?<value>[^"]*(?=")))|(?:\\s+(?:in|not in)\\s+(?:\\((?<values>.*)\\))))'

  const termsRegEx: RegExp = new RegExp(termExp, 'gi')
  const match = termsRegEx.exec(queryTerm)
  if (!match)
    return match;

  const groups : any = match.groups
  const searchObj : Record<string,string | string[]> = {};
  searchObj[groups.keyword] = groups.value? '"'+groups.value+'"': groups.values ? groups.values.split(/,\s*/) : '';
  return searchObj
}

const extractOperatorTerm = (operator: string): any => {
  const operatorExp = '(?<operator>(' + OPERATORS.join('|') + '))\\s*'

  const operatorRegEx : RegExp = new RegExp(operatorExp, 'gi')
  const match = operatorRegEx.exec(operator)
  if (!match)
    return match

  const groups : any = match.groups
  const operatorObj : Record<string, any> = {}
  operatorObj[groups.operator] = groups.operator.toLowerCase()
  return operatorObj;
}


const isKeyValuePresent = (searchObjs: any[], key:string, value: string): boolean => {
  if (!searchObjs || !key || !value)
    return false;
  if (searchObjs[0] === null) return false



  const searchObj= searchObjs.filter((o:any) => Object.keys(o)[0].toLowerCase() === key.toLowerCase())
  if (searchObj.length === 0)
    return false;

  value = '"'+value.toLowerCase()+'"'

  if (Array.isArray(searchObj[0][key.toLowerCase()]))
    return searchObj[0][key.toLowerCase()].indexOf(value) !== -1
  else
    return searchObj[0][key.toLowerCase()] === value

}

/**
 * This methodonvert an search obj array into a search sting which can be populated in the Chip input field
 */
function convertSearchObjsToQueryString(searchObjs: any[]): string {
  if (!searchObjs) return '';
  if(searchObjs.length === 0)
    return '';

  if (searchObjs.length === 1 && typeof searchObjs[0] === 'string')  // This is the case of "value" only search
    return searchObjs[0]

  let searchStrings = convertSearchObjsToSearchStrings(searchObjs)
  let queryString = searchStrings.join(' ').trim();
  return queryString;
}

function convertSearchObjsToSearchStrings(searchObjs: any[]): string[] {
  if(!searchObjs)
    return []
  if(searchObjs.length === 0)
    return [];
  if (searchObjs[0] === null) return searchObjs

  let searchStrings: string[] = searchObjs.map((o: any) => {

    if (typeof o === 'string')
      return o

    const objKeys = Object.keys(o);
    if (objKeys.length !== 1)
    {
      // We have issue as we have no keys or more than one key ... Let us bail out
      return ''
    }
    // Each object will have only one key
    const key = objKeys[0].toLowerCase().trim();
    const keywordIdx = KEYWORDS.indexOf(key)
    if (keywordIdx !== -1){
      // We will convert the keyword into key
      if (Array.isArray(o[objKeys[0]])){
        // We will convert any values with " in boundary
        let values = o[objKeys[0]].map((value: string) => {
          return value.replace(/^"/,'').replace(/"$/,'')
        })
        values =  values.map((v: string) => {
          return '"' + v + '"'
        })
        let array = [];
        array.push(KEYWORDS[keywordIdx])
        array.push('in')
        array.push('(' + values.join(',') + ')')
        return array.join(' ')
      }
      else {
        const value = o[objKeys[0]].replace(/^"/,'').replace(/"$/,'')
       return KEYWORDS[keywordIdx] + ':"' + value + '"'
      }
    }

    const operatorIdx = OPERATORS.indexOf(key)
    if (operatorIdx !== -1 ) {
      return OPERATORS[operatorIdx];
    }
    return '';
  })
  return searchStrings;
}


export {
  removeFromSearchObjsByIndex,
  updateSearchObjs,
  validateQueryString,
  convertSearchObjsToQueryString,
  convertSearchObjsToSearchStrings,
  extractQueryTerm,
  extractOperatorTerm,
  addToSearchObjs,
  isKeyValuePresent,
  parseQueryString,
}


/********************************
 *  HELPER METHODS
 ********************************/

function isOperatorObj(object: any): boolean{
  const key = Object.keys(object)[0].toLowerCase()
  return OPERATORS.indexOf(key) !== -1
}

function isOperator(input: string): boolean {
  return OPERATORS.indexOf(input.toLowerCase()) !== -1
}
